import { useState, useEffect, useCallback } from 'react';
import { fetchDashboardLayout } from '../../../../services/dashboardService';
import { BREAKPOINT_COLS, WIDGET_CLASS_CONSTRAINTS } from '../../constants';
import { buildStackedLayout, expandWidgetsToAvailableSpace } from '../../utils';

const getStorageKey = (userId, dashboardUuid) =>
  `wg_${userId ?? ''}_${dashboardUuid}`;

const parseLayoutsFromStorage = (key) => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (Array.isArray(parsed)) {
      return { lg: parsed };
    }
    if (parsed && typeof parsed === 'object' && parsed.lg) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
};

const saveLayoutsToStorage = (key, layouts) => {
  try {
    localStorage.setItem(key, JSON.stringify(layouts));
  } catch (err) {
    console.warn('[useWidgetGrid] Failed to save layouts to localStorage:', err);
  }
};

/**
 * Transforms a widget from the layout contract into a react-grid-layout item.
 * Merges layout position (x,y,w,h) with constraints (minW,maxW,minH,maxH).
 * Falls back to WIDGET_CLASS_CONSTRAINTS if the contract doesn't provide constraints.
 */
const buildLayoutItem = (widget) => {
  const { layout, constraints, instance_uuid } = widget;
  const fallbackConstraints = WIDGET_CLASS_CONSTRAINTS.CLASS_2_STANDARD || {};
  const resolvedConstraints = constraints || fallbackConstraints;

  return {
    i: instance_uuid,
    x: layout.x,
    y: layout.y,
    w: layout.w,
    h: layout.h,
    ...resolvedConstraints,
  };
};

/**
 * Merges stored layout positions with current widget constraints from the contract.
 * Stored layouts may have outdated constraints, so we always re-apply from contract.
 */
const mergeStoredWithConstraints = (storedItems, widgetsByUuid) => {
  if (!storedItems?.length) return storedItems;
  return storedItems.map((item) => {
    const widget = widgetsByUuid[item.i];
    if (!widget) return item;
    const resolvedConstraints = widget.constraints || WIDGET_CLASS_CONSTRAINTS.CLASS_2_STANDARD || {};
    return {
      ...item,
      ...resolvedConstraints,
      i: item.i,
      x: item.x,
      y: item.y,
      w: item.w,
      h: item.h,
    };
  });
};

const buildResponsiveLayouts = (lgLayout) => {
  if (!lgLayout?.length) return {};
  return {
    lg: lgLayout,
    md: lgLayout,
    sm: lgLayout,
    xs: buildStackedLayout(lgLayout, BREAKPOINT_COLS.xs),
    xxs: buildStackedLayout(lgLayout, BREAKPOINT_COLS.xxs),
  };
};

const applyExpandToLayouts = (layouts) => {
  if (!layouts) return layouts;
  const result = { ...layouts };
  const expandableBreakpoints = ['lg', 'md', 'sm'];
  for (const bp of expandableBreakpoints) {
    const layout = result[bp];
    if (layout?.length && BREAKPOINT_COLS[bp]) {
      result[bp] = expandWidgetsToAvailableSpace(layout, BREAKPOINT_COLS[bp]);
    }
  }
  return result;
};

export const useWidgetGrid = ({ dashboardUuid, userId }) => {
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(null);
  const [widgetConfig, setWidgetConfig] = useState(null);

  const storageKey = getStorageKey(userId, dashboardUuid);

  useEffect(() => {
    if (!dashboardUuid) {
      setConfigLoading(false);
      setConfigError(new Error('Missing required param: dashboardUuid'));
      return;
    }

    setConfigLoading(true);
    setConfigError(null);

    fetchDashboardLayout(dashboardUuid)
      .then((response) => {
        const dashboard = response.data;
        const contractWidgets = dashboard.widgets || [];

        const widgetsByUuid = Object.fromEntries(
          contractWidgets.map((w) => [w.instance_uuid, w]),
        );

        const defaultLayout = contractWidgets.map(buildLayoutItem);

        const storedLayouts = parseLayoutsFromStorage(storageKey);
        const rawLgLayout = storedLayouts?.lg || defaultLayout;
        const lgLayout = storedLayouts?.lg
          ? mergeStoredWithConstraints(rawLgLayout, widgetsByUuid)
          : defaultLayout;

        const layouts = buildResponsiveLayouts(lgLayout);

        setWidgetConfig({
          dashboardUuid: dashboard.dashboard_uuid,
          name: dashboard.name,
          version: dashboard.version,
          filters: dashboard.filters || [],
          gridConfig: { cols: 12, rowHeight: 24 },
          widgets: contractWidgets,
          layouts,
        });
      })
      .catch((err) => {
        console.error('[useWidgetGrid] fetchDashboardLayout error:', err);
        setConfigError(err);
        setWidgetConfig(null);
      })
      .finally(() => {
        setConfigLoading(false);
      });
  }, [dashboardUuid, storageKey]);

  const handleLayoutChange = useCallback(
    (newLayout, newLayouts) => {
      if (!newLayouts) return;

      setWidgetConfig((prev) => {
        if (!prev) return prev;
        const widgetsByUuid = Object.fromEntries(
          (prev.widgets || []).map((w) => [w.instance_uuid, w]),
        );
        const merged = {};
        for (const [bp, layout] of Object.entries(newLayouts)) {
          if (layout?.length) {
            merged[bp] = mergeStoredWithConstraints(layout, widgetsByUuid);
          } else {
            merged[bp] = layout;
          }
        }
        const expanded = applyExpandToLayouts(merged);
        saveLayoutsToStorage(storageKey, expanded);
        return { ...prev, layouts: expanded };
      });
    },
    [storageKey],
  );

  return {
    widgetConfig,
    configLoading,
    configError,
    handleLayoutChange,
  };
};
